import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useReveal from "@/hooks/useReveal";
import { listCaseStudies, loadSettings, CaseStudyRow } from "@/lib/api";
import { U } from "@/data/services";

const HERO_BG = U("1492684223066-81342ee5ff30", 2400, 88);

const CaseStudies: React.FC = () => {
  useReveal();
  const [comingSoon, setComingSoon] = useState<boolean | null>(null);
  const [studies, setStudies] = useState<CaseStudyRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    // Coming Soon setting is the source of truth for public visibility.
    // Absent key → default to true so the placeholder shows before the first study is published.
    loadSettings().then(s => {
      if (cancelled) return;
      const v = (s.case_studies_coming_soon ?? "true").toLowerCase();
      setComingSoon(v !== "false");
    }).catch(() => { if (!cancelled) setComingSoon(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (comingSoon === false) {
      // Only fetch published studies when the grid is actually going to render.
      listCaseStudies(true).then(rows => setStudies(rows)).catch(() => setStudies([]));
    }
  }, [comingSoon]);

  if (comingSoon === null) return <main className="iav-page" style={{ minHeight: "60vh" }} data-testid="case-studies-page" />;

  return (
    <main className="iav-page" data-testid="case-studies-page">
      <section className="iav-page-hero iav-hero-bg" style={{ backgroundImage: `url(${HERO_BG})` }}>
        <div className="iav-page-hero-overlay" />
        <div className="iav-page-hero-inner">
          <span className="iav-tag-line" data-reveal>Behind the Show</span>
          <h1 data-reveal>Case <span className="iav-accent">Studies.</span></h1>
          <p className="lead" data-reveal>
            {comingSoon
              ? "In-depth stories of our productions — coming soon."
              : "How we designed, planned and delivered our most technically demanding productions."}
          </p>
        </div>
      </section>

      {comingSoon ? (
        <section className="iav-section" data-testid="case-studies-coming-soon" style={{ textAlign: "center", padding: "clamp(80px, 12vw, 160px) var(--gutter)" }}>
          <span className="iav-tag-line" style={{ display: "block", marginBottom: 20 }} data-reveal>Coming Soon</span>
          <h2 className="iav-section-title" data-reveal style={{ marginBottom: 18 }}>Stories from the field, on the way.</h2>
          <p className="lead" data-reveal style={{ maxWidth: 640, margin: "0 auto" }}>
            We're preparing detailed case studies of our recent productions — the challenges, the design decisions and the equipment that made them possible. Check back soon.
          </p>
          <div style={{ marginTop: 36 }} data-reveal>
            <Link to="/gallery" className="iav-btn iav-btn-outline" data-testid="cs-back-to-gallery">Browse the Gallery</Link>
          </div>
        </section>
      ) : (
        <div className="iav-grid-3" data-testid="case-studies-grid">
          {studies.length === 0 && (
            <div style={{ gridColumn: "1 / -1", padding: "60px 20px", textAlign: "center", color: "rgba(255,255,255,0.6)" }} data-testid="case-studies-empty">
              No case studies published yet.
            </div>
          )}
          {studies.map(p => (
            <Link key={p.slug} to={`/case-studies/${p.slug}`} className="iav-gal-card" data-cursor="cta" data-testid={`case-study-card-${p.slug}`}>
              {p.hero_image_url && <img src={p.hero_image_url} alt={p.title} loading="lazy" />}
              <div className="iav-gal-overlay" />
              <div className="iav-gal-meta">
                <span className="iav-gal-cat">{[p.client, p.category].filter(Boolean).join(" · ")}</span>
                <span className="iav-gal-title">{p.title}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
};
export default CaseStudies;
